/**
 * Where the floating widget sits, resolved in one place so the bubble, the
 * window and `useResize` never disagree on side and bottom gap.
 */

/** Edge the widget anchors to. */
export type AnchorSide = 'left' | 'right';

/**
 * Half-screen maximize from the position menu. Contributes geometry only: the
 * side a snap implies is committed separately as the visitor's choice.
 */
export type SnapState = 'left-half' | 'right-half' | null;

/** Smallest usable panel. Shared with `useResize` so there is one such number. */
export const WIDGET_MIN_HEIGHT = 400;

/** Gaps applied when no bottom offset is configured. */
export const CLOSED_LEGACY_BOTTOM_PX = 20;
export const OPEN_LEGACY_BOTTOM_PX = 32;
export const CLOSED_LEGACY_INSET_PX = 20;
export const OPEN_LEGACY_INSET_PX = 32;

/** Range the admin can configure, matching the backend's validation bounds. */
const MIN_OFFSET_PERCENT = 0;
const MAX_OFFSET_PERCENT = 50;

/**
 * Percent of the bounds height to pixels. Zero is a deliberate flush gap, not
 * unset, which is why `useResize`'s `percentToPixels` cannot be reused.
 */
export function bottomOffsetToPx(
  percent: number | null | undefined,
  boundsHeight: number,
  legacyPx: number,
): number {
  if (percent === null || percent === undefined) return legacyPx;
  const clamped = Math.min(MAX_OFFSET_PERCENT, Math.max(MIN_OFFSET_PERCENT, percent));
  return Math.round((boundsHeight * clamped) / 100);
}

/** A host page's validated runtime `setPlacement({ bottomOffset })` override. */
export type RuntimeBottomOffset = { unit: 'px'; value: number } | { unit: 'percent'; value: number };

/**
 * Parse a host-supplied `setPlacement({ bottomOffset })` value. `undefined`
 * leaves the axis untouched, `null` resets it; anything unparseable is
 * `'invalid'`, since a raw `%` string reaching the DOM would disable the
 * resize clamp.
 */
export function parseRuntimeBottomOffset(raw: number | string | null): RuntimeBottomOffset | null | 'invalid';
export function parseRuntimeBottomOffset(
  raw: number | string | null | undefined,
): RuntimeBottomOffset | null | undefined | 'invalid';
export function parseRuntimeBottomOffset(
  raw: number | string | null | undefined,
): RuntimeBottomOffset | null | undefined | 'invalid' {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { unit: 'px', value: raw } : 'invalid';
  }
  const match = /^-?\d+(\.\d+)?%$/.exec(raw.trim());
  if (!match) return 'invalid';
  return { unit: 'percent', value: parseFloat(raw) };
}

/** The bottom gap before it is fitted to the bounds, shared with `useResize`. */
export function resolveDesiredBottomPx(
  runtimeBottomOffset: RuntimeBottomOffset | null | undefined,
  hostOffsetY: number | undefined,
  bottomOffsetPercent: number | null,
  boundsHeight: number,
  legacyPx: number,
): number {
  if (runtimeBottomOffset) {
    return runtimeBottomOffset.unit === 'px'
      ? runtimeBottomOffset.value
      : bottomOffsetToPx(runtimeBottomOffset.value, boundsHeight, legacyPx);
  }
  return hostOffsetY ?? bottomOffsetToPx(bottomOffsetPercent, boundsHeight, legacyPx);
}

/**
 * The bottom gap that actually fits: one that would squeeze the panel below
 * the minimum gives way, since the offset is decoration and the conversation
 * is not.
 */
export function fitBottomOffset(
  desiredBottomPx: number,
  boundsHeight: number,
  minHeight: number = WIDGET_MIN_HEIGHT,
): number {
  if (desiredBottomPx <= 0) return 0;
  if (boundsHeight - desiredBottomPx >= minHeight) return desiredBottomPx;
  return Math.max(0, boundsHeight - minHeight);
}

/** The open-panel height that fits above an already-fitted bottom gap. */
export function fitPanelHeight(
  requestedHeight: number,
  bottomPx: number,
  boundsHeight: number,
  minHeight: number = WIDGET_MIN_HEIGHT,
): number {
  return Math.min(requestedHeight, Math.max(minHeight, boundsHeight - bottomPx));
}

export interface WidgetAnchorInput {
  isOpen: boolean;
  isMobile: boolean;
  previewMode: boolean;
  /** Currently maximized to half the bounds. */
  snapState: SnapState;
  /** Side the visitor explicitly moved to, restored from storage. */
  visitorSide: AnchorSide | null;
  /** Embed-snippet override (`config.position`), production only. */
  hostSide?: AnchorSide;
  /** Embed-snippet overrides (`config.offset`), in px, production only. */
  hostOffsetX?: number;
  hostOffsetY?: number;
  /**
   * Host page's runtime `KapiaWidget.setPlacement({ side })` override,
   * production only. Outranks even the visitor's stored side — the host is
   * reacting to its own layout, not offering the visitor a choice.
   */
  runtimeSide?: AnchorSide | null;
  /** Host page's runtime `setPlacement({ bottomOffset })` override, production only. */
  runtimeBottomOffset?: RuntimeBottomOffset | null;
  /** Side the admin configured, served with the widget config. */
  defaultSide: AnchorSide;
  /** Gap the admin configured, as a percent of the bounds height. */
  bottomOffsetPercent: number | null;
  /** Viewport height, or the preview container's height in preview mode. */
  boundsHeight: number;
}

export interface WidgetAnchorResult {
  side: AnchorSide;
  bottomPx: number;
  insetPx: number;
  /** Pinned to the bounds: snapped, or open on mobile. Both gaps are zero. */
  fullBleed: boolean;
}

/**
 * Resolve the side and the gaps for the current state. The visitor's side
 * stays null until they move the window, so a changed admin default reaches
 * everyone who never did.
 */
export function resolveWidgetAnchor(input: WidgetAnchorInput): WidgetAnchorResult {
  // The snippet overrides describe a host page. Inside the admin preview the
  // "host" is our own wrapper component, so honouring them there would let a
  // placeholder value mask the very setting being configured.
  const hostSide = input.previewMode ? undefined : input.hostSide;
  const hostOffsetX = input.previewMode ? undefined : input.hostOffsetX;
  const hostOffsetY = input.previewMode ? undefined : input.hostOffsetY;
  const runtimeSide = input.previewMode ? undefined : input.runtimeSide;
  const runtimeBottomOffset = input.previewMode ? undefined : input.runtimeBottomOffset;

  const side: AnchorSide = runtimeSide ?? input.visitorSide ?? hostSide ?? input.defaultSide;

  // Snapped and mobile-open panels fill their bounds by design; they ignore
  // both gaps rather than clamping them.
  if (input.isOpen && (input.snapState !== null || input.isMobile)) {
    return {
      side:
        input.snapState === 'left-half'
          ? 'left'
          : input.snapState === 'right-half'
            ? 'right'
            : side,
      bottomPx: 0,
      insetPx: 0,
      fullBleed: true,
    };
  }

  const legacyBottom = input.isOpen ? OPEN_LEGACY_BOTTOM_PX : CLOSED_LEGACY_BOTTOM_PX;
  const desiredBottom = resolveDesiredBottomPx(
    runtimeBottomOffset,
    hostOffsetY,
    input.bottomOffsetPercent,
    input.boundsHeight,
    legacyBottom,
  );

  // Only the open panel has a height worth protecting; the closed bubble is
  // roughly 60px and always fits.
  const bottomPx = input.isOpen
    ? fitBottomOffset(desiredBottom, input.boundsHeight)
    : desiredBottom;

  const insetPx =
    hostOffsetX ?? (input.isOpen ? OPEN_LEGACY_INSET_PX : CLOSED_LEGACY_INSET_PX);

  return { side, bottomPx, insetPx, fullBleed: false };
}
